import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/api/client";
import type { UserOrgsResponse } from "@/api/types";

type Status = "checking" | "ok" | "redirect";

/**
 * Reconciles the `:orgSlug` URL segment with the user's actual active
 * organization (server-side session state).
 *
 * - Slug matches the active org (`user.org_slug`) -> renders children.
 * - Slug matches a different org the user has an active membership in ->
 *   switches the active org server-side (`switch-org` + `refreshAuth`) and
 *   forces a full page reload to `/${orgSlug}`. A full reload is required
 *   (rather than rendering children in place) because `refreshAuth()` only
 *   re-fetches the current user; it does not invalidate other in-memory
 *   data (project lists, members, etc.) held by other mounted components,
 *   which would otherwise keep showing stale data from the previous org.
 * - Slug is nonexistent, not a membership of the user, or an inactive
 *   membership, or the switch itself fails -> redirects to `/choose-org`.
 */
export function OrgGuard({ children }: { children: React.ReactNode }) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { user, refreshAuth } = useAuth();
  const [status, setStatus] = useState<Status>(
    user && orgSlug === user.org_slug ? "ok" : "checking",
  );

  useEffect(() => {
    if (!user || orgSlug === user.org_slug) {
      setStatus("ok");
      return;
    }

    let cancelled = false;
    setStatus("checking");

    api<UserOrgsResponse>("/api/v2/user/orgs")
      .then(async (data) => {
        const membership = data.orgs.find((org) => org.slug === orgSlug);
        if (!membership) {
          if (!cancelled) setStatus("redirect");
          return;
        }

        await api("/api/v2/auth/switch-org", {
          method: "POST",
          body: JSON.stringify({ org_id: membership.org_id }),
        });
        await refreshAuth();
        if (!cancelled) {
          window.location.href = `/${orgSlug}`;
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("redirect");
      });

    return () => {
      cancelled = true;
    };
  }, [orgSlug, user, refreshAuth]);

  if (status === "redirect") {
    return <Navigate to="/choose-org" replace />;
  }

  if (status !== "ok") {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return children;
}
